import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Logo from '../components/Logo';
import { getApiUrl } from '../config/api.config';

const Signup = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        tenantName: '',
        tenantIdentifier: '',
        companyPan: '',
        bankAccountNumber: '',
        confirmBankAccountNumber: '',
        ifscCode: '',
        bankName: '',
        branchName: '',
        accountHolderName: ''
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [ifscVerifying, setIfscVerifying] = useState(false);
    const [ifscVerified, setIfscVerified] = useState(false);
    const [ifscError, setIfscError] = useState('');
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Convert PAN and IFSC to uppercase
        const processedValue = (name === 'companyPan' || name === 'ifscCode')
            ? value.toUpperCase()
            : value;

        setFormData({
            ...formData,
            [name]: processedValue
        });

        // Reset IFSC verification when IFSC code changes
        if (name === 'ifscCode') {
            setIfscVerified(false);
            setIfscError('');
        }
    };

    const verifyIFSC = async () => {
        if (!formData.ifscCode || formData.ifscCode.length !== 11) {
            setIfscError('IFSC code must be 11 characters');
            return;
        }

        setIfscVerifying(true);
        setIfscError('');

        try {
            // Using IFSC API for verification
            const response = await fetch(`https://ifsc.razorpay.com/${formData.ifscCode}`);

            if (response.ok) {
                const data = await response.json();
                setFormData({
                    ...formData,
                    bankName: data.BANK || '',
                    branchName: data.BRANCH || ''
                });
                setIfscVerified(true);
                setIfscError('');
            } else {
                setIfscError('Invalid IFSC code. Please check and try again.');
                setIfscVerified(false);
            }
        } catch (error) {
            setIfscError('Failed to verify IFSC code. Please try again.');
            setIfscVerified(false);
        } finally {
            setIfscVerifying(false);
        }
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setError('');

        // Validation
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        // Validate PAN format (10 characters: 5 letters, 4 digits, 1 letter)
        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
        if (!panRegex.test(formData.companyPan)) {
            setError('Invalid PAN format. Format: ABCDE1234F');
            return;
        }

        // Validate bank account numbers match
        if (formData.bankAccountNumber !== formData.confirmBankAccountNumber) {
            setError('Bank account numbers do not match');
            return;
        }

        // Validate IFSC is verified
        if (!ifscVerified) {
            setError('Please verify your IFSC code before submitting');
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(getApiUrl('auth/signup'), {
                name: formData.name,
                email: formData.email,
                password: formData.password,
                tenantName: formData.tenantName,
                tenantIdentifier: formData.tenantIdentifier,
                companyPan: formData.companyPan,
                bankAccountNumber: formData.bankAccountNumber,
                ifscCode: formData.ifscCode,
                bankName: formData.bankName,
                branchName: formData.branchName,
                accountHolderName: formData.accountHolderName
            });

            setSuccess(true);
            // Redirect to tenant-specific login after 2 seconds
            setTimeout(() => {
                navigate(`/${formData.tenantIdentifier}`, {
                    state: {
                        email: formData.email,
                        message: 'Account created successfully! Please login.'
                    }
                });
            }, 2000);
        } catch (err) {
            setSuccess(false); // Clear success state
            // Extract detailed error message
            const errorMessage = err.response?.data?.message
                || err.response?.data?.error
                || err.message
                || 'Signup failed. Please try again.';
            setError(errorMessage);
            console.error('Signup error:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                    <div className="px-10 py-12">
                        <div className="text-center mb-8">
                            <div className="flex justify-center items-center mb-6">
                                <Logo showText={true} width={80} height={80} className="justify-center" />
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900 mb-2">
                                Create Account
                            </h2>
                            <p className="text-gray-600 mb-1">
                                Join iVault - Your Trusted Digital Vault
                            </p>
                            <p className="text-sm text-gray-500">
                                a product of Techpuram
                            </p>
                        </div>

                        <form className="space-y-6" onSubmit={handleSignup}>
                            {error && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center">
                                            <svg className="w-5 h-5 text-red-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                            <span className="text-sm text-red-800">{error}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setError('')}
                                            className="text-red-400 hover:text-red-600 transition-colors"
                                        >
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {success && (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4 animate-fade-in">
                                    <div className="flex items-center">
                                        <svg className="w-5 h-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        <div>
                                            <span className="text-sm font-semibold text-green-800">Account created successfully!</span>
                                            <p className="text-xs text-green-600 mt-1">Redirecting to your login page...</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-5">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                        Full Name
                                    </label>
                                    <input
                                        id="name"
                                        name="name"
                                        type="text"
                                        required
                                        value={formData.name}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900"
                                        placeholder="John Doe"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                        Email Address
                                    </label>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900"
                                        placeholder="john@example.com"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="tenantName" className="block text-sm font-medium text-gray-700 mb-2">
                                        Company/Organization Name
                                    </label>
                                    <input
                                        id="tenantName"
                                        name="tenantName"
                                        type="text"
                                        required
                                        value={formData.tenantName}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900"
                                        placeholder="Acme Corp"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="tenantIdentifier" className="block text-sm font-medium text-gray-700 mb-2">
                                        Company Identifier (lowercase, no spaces)
                                    </label>
                                    <input
                                        id="tenantIdentifier"
                                        name="tenantIdentifier"
                                        type="text"
                                        required
                                        value={formData.tenantIdentifier}
                                        onChange={handleChange}
                                        pattern="[a-z0-9-]+"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900"
                                        placeholder="acme-corp"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        This will be your unique identifier (e.g., acme-corp)
                                    </p>
                                </div>

                                {/* Company PAN */}
                                <div>
                                    <label htmlFor="companyPan" className="block text-sm font-medium text-gray-700 mb-2">
                                        Company PAN <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="companyPan"
                                        name="companyPan"
                                        type="text"
                                        required
                                        maxLength="10"
                                        value={formData.companyPan}
                                        onChange={handleChange}
                                        pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900 uppercase"
                                        placeholder="ABCDE1234F"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        Format: 5 letters, 4 digits, 1 letter (e.g., ABCDE1234F)
                                    </p>
                                </div>

                                {/* Bank Account Details Section */}
                                <div className="pt-4 border-t border-gray-200">
                                    <h3 className="text-sm font-semibold text-gray-900 mb-4">Bank Account Details</h3>

                                    {/* Account Holder Name */}
                                    <div className="mb-4">
                                        <label htmlFor="accountHolderName" className="block text-sm font-medium text-gray-700 mb-2">
                                            Account Holder Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="accountHolderName"
                                            name="accountHolderName"
                                            type="text"
                                            required
                                            value={formData.accountHolderName}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900"
                                            placeholder="As per bank records"
                                        />
                                    </div>

                                    {/* Bank Account Number */}
                                    <div className="mb-4">
                                        <label htmlFor="bankAccountNumber" className="block text-sm font-medium text-gray-700 mb-2">
                                            Bank Account Number <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="bankAccountNumber"
                                            name="bankAccountNumber"
                                            type="text"
                                            required
                                            value={formData.bankAccountNumber}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900"
                                            placeholder="Enter account number"
                                        />
                                    </div>

                                    {/* Confirm Bank Account Number */}
                                    <div className="mb-4">
                                        <label htmlFor="confirmBankAccountNumber" className="block text-sm font-medium text-gray-700 mb-2">
                                            Confirm Account Number <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="confirmBankAccountNumber"
                                            name="confirmBankAccountNumber"
                                            type="text"
                                            required
                                            value={formData.confirmBankAccountNumber}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900"
                                            placeholder="Re-enter account number"
                                        />
                                    </div>

                                    {/* IFSC Code with Verification */}
                                    <div className="mb-4">
                                        <label htmlFor="ifscCode" className="block text-sm font-medium text-gray-700 mb-2">
                                            IFSC Code <span className="text-red-500">*</span>
                                        </label>
                                        <div className="flex space-x-2">
                                            <input
                                                id="ifscCode"
                                                name="ifscCode"
                                                type="text"
                                                required
                                                maxLength="11"
                                                value={formData.ifscCode}
                                                onChange={handleChange}
                                                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900 uppercase"
                                                placeholder="SBIN0001234"
                                            />
                                            <button
                                                type="button"
                                                onClick={verifyIFSC}
                                                disabled={ifscVerifying || !formData.ifscCode || formData.ifscCode.length !== 11}
                                                className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center space-x-2"
                                            >
                                                {ifscVerifying ? (
                                                    <>
                                                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        <span>Verifying...</span>
                                                    </>
                                                ) : ifscVerified ? (
                                                    <>
                                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                        </svg>
                                                        <span>Verified</span>
                                                    </>
                                                ) : (
                                                    <span>Verify</span>
                                                )}
                                            </button>
                                        </div>
                                        {ifscError && (
                                            <p className="mt-1 text-xs text-red-600">{ifscError}</p>
                                        )}
                                        {ifscVerified && (
                                            <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                                                <p className="text-xs text-green-800">
                                                    <span className="font-semibold">Bank:</span> {formData.bankName}
                                                </p>
                                                <p className="text-xs text-green-800">
                                                    <span className="font-semibold">Branch:</span> {formData.branchName}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                                        Password
                                    </label>
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        required
                                        value={formData.password}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900"
                                        placeholder="••••••••"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                                        Confirm Password
                                    </label>
                                    <input
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        type="password"
                                        required
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900"
                                        placeholder="••••••••"
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Creating Account...' : 'Create Account'}
                            </button>

                            <div className="text-center mt-6">
                                <p className="text-sm text-gray-600">
                                    Already have an account?{' '}
                                    <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
                                        Sign in
                                    </Link>
                                </p>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="mt-8 text-center">
                    <p className="text-sm text-gray-600">
                        By signing up, you agree to our Terms of Service and Privacy Policy
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Signup;
